import { useCallback, useEffect, useRef, useState } from "react";
import { dark_mode_primary, dark_mode_secondary, light_mode_primary, AppMode } from "./values";
import { ActivityGraph, GeminiChat, Gmail, Outlook, ProfileHeader, RunningTasks, TaskOverview, WeekCalendar, WidgetSelectionPopup } from "./widgets";

export function Overview({ setAuthStatus, appMode, events, setEvents, setError, authManager, setActiveTab, toggleAppMode, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, subscriptionTier }) {
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [isWidgetPopupOpen, setIsWidgetPopupOpen] = useState(false);

    const handleAddWidget = useCallback((selectedWidget) => {
        // const newWidget = {
        //     id: selectedWidget.id,
        //     component: getComponentById(selectedWidget.id),
        //     props: getPropsById(selectedWidget.id),
        //     wrapperClass: "px-5 py-3"
        // };
        // setWidgets(prevWidgets => [...prevWidgets, newWidget]);
        // setIsWidgetPopupOpen(false);//eslint-disable-next-line
    }, []);

    const getAvailableWidgets = useCallback(() => {
        const baseWidgets = [
            { id: "gemini", component: GeminiChat, props: { appMode, subscriptionTier }, wrapperClass: "px-5" },
            {
                id: "runningTasks-activity", component: null, isGroup: true, wrapperClass: "py-14 w-full flex items-center", children: [
                    { id: "runningTasks", component: RunningTasks, props: { appMode } },
                    { id: "activityGraph", component: ActivityGraph, props: { appMode } }
                ]
            },
            {
                id: "outlook-gmail", component: null, isGroup: true, wrapperClass: "w-full h-4/5 flex items-center", children: [
                    { id: "outlook", component: Outlook, props: { appMode, authManager, setIsMicrosoftLoggedIn } },
                    { id: "gmail", component: Gmail, props: { appMode, authManager, setIsGoogleLoggedIn } },
                ]
            },
        ];
        return baseWidgets;//eslint-disable-next-line
    }, [appMode, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, subscriptionTier]);

    const getComponentById = (id) => {
        switch (id) {
            case 'gemini': return GeminiChat;
            case 'runningTasks': return RunningTasks;
            case 'activityGraph': return ActivityGraph;
            case 'outlook': return Outlook;
            case 'gmail': return Gmail;
            default: return null;
        }
    };

    const getPropsById = useCallback((id) => {
        switch (id) {
            case 'outlook': return { authManager, setIsMicrosoftLoggedIn, appMode, subscriptionTier };
            case 'gmail': return { authManager, setIsGoogleLoggedIn, appMode, subscriptionTier };
            default: return { appMode, subscriptionTier };
        }//eslint-disable-next-line
    }, [appMode, setIsGoogleLoggedIn, setIsMicrosoftLoggedIn, subscriptionTier]);

    const updateWidgetsWithProps = useCallback((widgetsList) => {
        return widgetsList.map(widget => {
            if (widget.isGroup) {
                return {
                    ...widget,
                    children: widget.children.map(child => ({
                        ...child,
                        component: getComponentById(child.id),
                        props: getPropsById(child.id)
                    }))
                };
            } else {
                return {
                    ...widget,
                    component: getComponentById(widget.id),
                    props: getPropsById(widget.id)
                };
            }
        });
    }, [getPropsById]);

    const [widgets, setWidgets] = useState(() => {
        if (typeof window === 'undefined' || !window.localStorage) {
            return getAvailableWidgets();
        }
        const savedWidgets = window.localStorage.getItem('dashboardWidgets');
        if (savedWidgets) {
            try {
                const parsedWidgets = JSON.parse(savedWidgets);
                return updateWidgetsWithProps(parsedWidgets);
            } catch (e) {
                return getAvailableWidgets();
            }
        }
        return getAvailableWidgets();
    });

    useEffect(() => {
        setWidgets(prevWidgets => updateWidgetsWithProps(prevWidgets));
    }, [appMode, updateWidgetsWithProps]);

    useEffect(() => {
        if (subscriptionTier !== undefined) {
            setWidgets(getAvailableWidgets());
        }
    }, [subscriptionTier, getAvailableWidgets]);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
            const serializableWidgets = widgets.map(widget => {
                if (widget.isGroup) {
                    return {
                        ...widget,
                        children: widget.children.map(child => ({
                            id: child.id,
                            wrapperClass: child.wrapperClass,
                        }))
                    };
                } else {
                    return {
                        id: widget.id,
                        wrapperClass: widget.wrapperClass,
                        isGroup: widget.isGroup,
                    };
                }
            });
            window.localStorage.setItem('dashboardWidgets', JSON.stringify(serializableWidgets));
        }
    }, [widgets]);

    const toggleReorderMode = () => {
        setIsReorderMode(!isReorderMode);
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    const handleDragStart = (e, index) => {
        if (!isReorderMode) return;
        setDraggedItem(index);
        const dragImg = document.createElement('div');
        dragImg.style.opacity = '0';
        document.body.appendChild(dragImg);
        e.dataTransfer.setDragImage(dragImg, 0, 0);
        document.body.removeChild(dragImg);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (!isReorderMode || draggedItem === null) return;
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedItem(null);
        setDragOverIndex(null);
    };

    const handleDrop = useCallback((e, dropIndex) => {
        e.preventDefault();
        if (!isReorderMode || draggedItem === null || draggedItem === dropIndex) {
            setDraggedItem(null);
            setDragOverIndex(null);
            return;
        }
        const newWidgets = [...widgets];
        const itemToMove = newWidgets[draggedItem];
        newWidgets.splice(draggedItem, 1);
        newWidgets.splice(dropIndex, 0, itemToMove);
        setWidgets(newWidgets);
        setDraggedItem(null);
        setDragOverIndex(null);
    }, [widgets, draggedItem, isReorderMode]);

    const resetLayout = () => {
        setWidgets(getAvailableWidgets());
        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.removeItem('dashboardWidgets');
        }
    };

    const renderWidgets = () => {
        return widgets.map((widget, index) => {
            const isDragging = draggedItem === index;
            const isOver = dragOverIndex === index;
            const dragStyles = isReorderMode
                ? `border ${isDragging ? 'border-blue-500 opacity-50' : 'border-dashed border-blue-300'} 
                 ${isOver ? 'border-blue-500 bg-blue-50' : ''} rounded relative transition-all duration-200`
                : '';
            if (widget.isGroup) {
                return (
                    <div key={widget.id} className={`${widget.wrapperClass} ${dragStyles}`} draggable={isReorderMode} onDragStart={(e) => handleDragStart(e, index)} onDragOver={(e) => handleDragOver(e, index)} onDragEnd={handleDragEnd} onDrop={(e) => handleDrop(e, index)}>
                        {isReorderMode && (
                            <div className="absolute top-2 right-2 bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded shadow-sm z-10 flex items-center gap-1 select-none">
                                <i className="bi bi-grip-vertical"></i><span>Drag</span>
                            </div>
                        )}
                        {widget.children.map(child => {
                            const ChildComponent = child.component;
                            return <ChildComponent key={child.id} {...child.props} />;
                        })}
                    </div>
                );
            } else {
                const WidgetComponent = widget.component;
                return (
                    <div key={widget.id} className={`${widget.wrapperClass} ${dragStyles}`} draggable={isReorderMode} onDragStart={(e) => handleDragStart(e, index)} onDragOver={(e) => handleDragOver(e, index)} onDragEnd={handleDragEnd} onDrop={(e) => handleDrop(e, index)}>
                        {isReorderMode && (
                            <div className="absolute top-2 right-2 bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded shadow-sm z-10 flex items-center gap-1 select-none">
                                <i className="bi bi-grip-vertical"></i><span>Drag</span>
                            </div>
                        )}
                        <WidgetComponent {...widget.props} />
                    </div>
                );
            }
        });
    };
    return (
        <div className={`flex h-full ${appMode === AppMode.LIGHT ? light_mode_primary : dark_mode_primary}`}>
            <div className="pt-3 pb-1 px-2 w-2/3 rounded">
                <div className={`h-full ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_secondary} rounded-lg flex-col overview overflow-y-auto`}>
                    <ProfileHeader appMode={appMode} subtitle={true} setAuthStatus={setAuthStatus} toggleAppMode={toggleAppMode} isReorderMode={isReorderMode} toggleReorderMode={toggleReorderMode} resetLayout={resetLayout} subscriptionTier={subscriptionTier} />
                    {renderWidgets()}
                    {isReorderMode && (
                        <div className="p-3 w-full h-20 text-center">
                            <button className={`w-1/2 h-full ${appMode === AppMode.LIGHT ? light_mode_primary + ' text-black' : dark_mode_primary + ' text-white'} rounded-lg`} onClick={() => setIsWidgetPopupOpen(true)}>
                                <i className="mr-2 bi bi-plus-lg"></i> Add a New Widget
                            </button>
                        </div>
                    )}
                    <WidgetSelectionPopup isOpen={isWidgetPopupOpen} onClose={() => setIsWidgetPopupOpen(false)} onAddWidget={handleAddWidget} appMode={appMode} />
                </div>
            </div>
            <div className="pt-2 pr-2 w-1/3 rounded">
                <WeekCalendar appMode={appMode} events={events} setEvents={setEvents} setError={setError} setActiveTab={setActiveTab} subscriptionTier={subscriptionTier} />
                <TaskOverview appMode={appMode} setActiveTab={setActiveTab} subscriptionTier={subscriptionTier} />
            </div>
        </div>
    );
}